<script setup lang="ts">
import IconScale from '~icons/mdi/brightness-percent'

interface ScalePrice {
  minQuantity: number
  price: number
}

const props = defineProps<{
  isScalePriceSheetOpen: boolean
  scalePrices: ScalePrice[]
  scaleAddToCart: (quantity: number) => Promise<void>
  maxQuantity?: number
}>()

const emit = defineEmits(['update:isScalePriceSheetOpen'])
const { currencyToString } = useHelper()

const availableScalePrices = computed(() =>
  props.scalePrices.filter(scale => !props.maxQuantity || scale.minQuantity <= props.maxQuantity),
)
</script>

<template>
  <v-bottom-sheet
    :value="props.isScalePriceSheetOpen"
    :model-value="props.isScalePriceSheetOpen"
    @update:model-value="emit('update:isScalePriceSheetOpen', false)"
  >
    <v-card>
      <!-- Header with headline and subtext -->
      <v-col
        cols="12"
        sm="10"
        md="6"
        class="mx-auto mt-4"
      >
        <div class="text-h5 mb-2">
          <v-icon :icon="IconScale" color="primary" />
          {{ $t('scaleDiscount.headline') }}
        </div>
        <div>
          {{ $t('scaleDiscount.text') }}
        </div>
      </v-col>

      <!-- Scale price list -->
      <v-row>
        <v-col
          cols="12"
          sm="10"
          md="6"
          class="mx-auto"
        >
          <v-sheet>
            <v-col
              v-for="(scalePrice, index) in availableScalePrices.filter(s => s.minQuantity !== 1)"
              :key="`scale-price-${index}`"
              cols="12"
              :class="{ 'bg-pale-grey': index % 2 === 0 }"
            >
              <div class="d-flex align-center">
                <span class="font-weight-bold mr-2">
                  {{ $t('scaleDiscount.piece', { quantity: scalePrice.minQuantity || 1 }) }}
                  <span class="text-primary">
                    {{ currencyToString(scalePrice.price) }}/
                  </span>
                  {{ $t('scaleDiscount.perPiece') }}
                </span>
                <!-- Add to cart button -->
                <v-btn
                  v-if="scalePrice.minQuantity > 0"
                  color="primary"
                  class="ml-auto"
                  width="250"
                  data-track-id="product_scale_add_to_cart"
                  @click="scaleAddToCart(scalePrice.minQuantity)"
                >
                  {{ $t('scaleDiscount.addToCart', { quantity: scalePrice.minQuantity }) }}
                </v-btn>
              </div>
            </v-col>
          </v-sheet>
        </v-col>
      </v-row>
    </v-card>
  </v-bottom-sheet>
</template>
